<template>
    <RegularModal ref="modal" @closed="emitClose">
        <template v-slot:title>
            <h4>
                {{ isEditMode ? translate("page.registration_stage.edit_stage") : translate("page.registration_stage.add_stage") }}
            </h4>
        </template>

        <template v-slot:body>
            <div v-if="isEditMode">
                <div class="field">
                    <label class="label">{{ translate("page.registration_stage.start") }}</label>
                    <NativeDateTimePicker v-model="editedStartDate" />
                </div>
                <div class="field">
                    <label class="label">{{ translate("page.registration_stage.end") }}</label>
                    <NativeDateTimePicker v-model="editedEndDate" />
                </div>
            </div>
            <div v-else>
                <div class="field">
                    <label class="label">{{ translate("page.registration_stage.total_days") }}</label>
                    <div class="control">
                        <NumberInput min="1" max="99" v-model="numberOfDays" />
                    </div>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <LoadingButton :is-loading="savingInProgress" class="button is-success" :disabled="!isValid" @click="save">
                    <span class="icon">
                        <i class="fas fa-check"></i>
                    </span>
                    <span>
                        {{ translate("common.save") }}
                    </span>
                </LoadingButton>
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import NativeDateTimePicker from "../../Common/NativeDateTimePicker.vue";
    import NumberInput from "../../Common/NumberInput.vue";
    import registrationStagesApi, { RegistrationStage } from "../../Api/registrationStages";
    import { handleError } from "../../Common/errorHandler";
    import { translate } from "../../Common/translations";
    import { convertFromTimezone, convertToTimezone } from "../../Common/dateTimeUtils";
    import { successToast } from "../../Common/toasts";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const emit = defineEmits(["closed", "saved"]);

    const modal = ref();
    const savingInProgress = ref(false);
    const isEditMode = ref(false);
    const currentStage = ref<RegistrationStage | undefined | null>(null);

    // For edit mode
    const editedStartDate = ref("");
    const editedEndDate = ref("");

    // For add mode
    const numberOfDays = ref(1);

    const isValid = computed(() => {
        if (isEditMode.value) {
            return editedStartDate.value && editedEndDate.value;
        } else {
            return numberOfDays.value > 0;
        }
    });

    const open = (stage?: RegistrationStage) => {
        if (stage) {
            isEditMode.value = true;
            currentStage.value = stage;
            editedStartDate.value = convertToTimezone(stage.startDate, props.eventTimezone);
            editedEndDate.value = convertToTimezone(stage.endDate, props.eventTimezone);
        } else {
            isEditMode.value = false;
            currentStage.value = null;
            numberOfDays.value = 1;
        }
        modal.value.open();
    };

    defineExpose({ open });

    const close = () => {
        modal.value.close();
    };

    const emitClose = () => {
        emit("closed");
    };

    const save = () => {
        if (isEditMode.value) {
            saveEdit();
        } else {
            saveAdd();
        }
    };

    const saveEdit = () => {
        if (!currentStage.value) {
            return Promise.reject("No current stage to edit");
        }

        savingInProgress.value = true;
        return registrationStagesApi
            .editRegistrationStage(
                currentStage.value.id,
                props.eventId,
                convertFromTimezone(editedStartDate.value, props.eventTimezone),
                convertFromTimezone(editedEndDate.value, props.eventTimezone),
            )
            .then(() => {
                successToast(translate("common.changes_have_been_saved"));
                emit("saved");
                close();
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    };

    const saveAdd = () => {
        savingInProgress.value = true;

        return registrationStagesApi
            .addAnotherRegistrationStage(props.eventId, {
                duration: numberOfDays.value * 24 * 60 * 60,
            })
            .then(() => {
                successToast(translate("common.changes_have_been_saved"));
                emit("saved");
                close();
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    };
</script>
